import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { 
  ChevronRight, ChevronLeft, Wand2, Eye, RotateCcw, Check,
  DoorOpen, Archive, Square, Blinds, GlassWater, Ruler, Package,
  PaintBucket, Wrench, Sparkles, Home, UtensilsCrossed, Shirt,
  Calculator, CircleDollarSign
} from 'lucide-react';
import { formatCurrency } from '@/utils/formatters';

// Tipos de móvel disponíveis
const TIPOS_MOVEL = [
  { 
    id: 'armario_pia', 
    label: 'Armário de Pia', 
    icon: UtensilsCrossed,
    desc: 'Armário embaixo da pia da cozinha',
    defaultLargura: 120,
    defaultAltura: 80,
    defaultProfundidade: 55,
    sugereGavetas: true,
    sugereNicho: false,
    maxPortas: 4,
    maxGavetas: 4
  },
  { 
    id: 'armario_cima_pia', 
    label: 'Armário em Cima da Pia', 
    icon: Package,
    desc: 'Armário aéreo acima da pia',
    defaultLargura: 120,
    defaultAltura: 70,
    defaultProfundidade: 35,
    sugereGavetas: false,
    sugereNicho: true,
    maxPortas: 4,
    maxGavetas: 0
  },
  { 
    id: 'armario_cozinha', 
    label: 'Armário Completo de Cozinha', 
    icon: Home,
    desc: 'Armário alto com portas e gavetas',
    defaultLargura: 160,
    defaultAltura: 220,
    defaultProfundidade: 55,
    sugereGavetas: true,
    sugereNicho: true,
    maxPortas: 6,
    maxGavetas: 6
  },
  { 
    id: 'guarda_roupa', 
    label: 'Guarda-Roupa', 
    icon: Shirt,
    desc: 'Guarda-roupa para quarto',
    defaultLargura: 200,
    defaultAltura: 240,
    defaultProfundidade: 60,
    sugereGavetas: true,
    sugereNicho: true,
    maxPortas: 6,
    maxGavetas: 8
  },
];

// Modelos de porta disponíveis
const MODELOS_PORTA = [
  { id: 'ripada', label: 'Porta Ripada', icon: Blinds, desc: 'Ripas verticais de madeira' },
  { id: 'lisa', label: 'Porta Lisa', icon: DoorOpen, desc: 'Porta de madeira maciça' },
  { id: 'vidro', label: 'Porta com Vidro', icon: GlassWater, desc: 'Moldura de madeira com vidro' },
];

// Tipos de dobradiça
const TIPOS_DOBRADICA = [
  { id: 'caneco_35mm', label: 'Caneco 35mm', preco: 8 },
  { id: 'caneco_26mm', label: 'Caneco 26mm', preco: 6 },
  { id: 'piano', label: 'Dobradiça Piano', preco: 25 },
  { id: 'invisivel', label: 'Dobradiça Invisível', preco: 35 },
];

// Tipos de puxador
const TIPOS_PUXADOR = [
  { id: 'cava', label: 'Cava (embutido)', preco: 15 },
  { id: 'alca_madeira', label: 'Alça de Madeira', preco: 12 },
  { id: 'alca_metal', label: 'Alça de Metal', preco: 18 },
  { id: 'botao', label: 'Botão', preco: 8 },
  { id: 'perfil', label: 'Perfil de Alumínio', preco: 45 },
];

// Tipos de corrediça
const TIPOS_CORREDICA = [
  { id: 'telescopica', label: 'Telescópica', preco: 45 },
  { id: 'oculta', label: 'Oculta (soft close)', preco: 85 },
  { id: 'simples', label: 'Simples', preco: 25 },
];

const STEPS = [
  { id: 'tipo', label: 'Tipo de Móvel', icon: Package },
  { id: 'dimensoes', label: 'Dimensões', icon: Ruler },
  { id: 'config', label: 'Configuração', icon: Wand2 },
  { id: 'materiais', label: 'Materiais', icon: PaintBucket },
  { id: 'resumo', label: 'Resumo', icon: Eye },
];

export default function ProjetoWizard({ 
  config, // Configuração de materiais/ferragens do sistema
  customers = [],
  onComplete, // Callback quando wizard completa
  onCancel,
  initialData = null // Para edição
}) {
  const [currentStep, setCurrentStep] = useState(0);
  
  // Estado do formulário
  const [formData, setFormData] = useState({
    // Step 1: Tipo
    tipoMovel: '',
    cliente_id: '',
    ambiente: 'Cozinha',
    nome_projeto: '',
    
    // Step 2: Dimensões
    largura: 120,
    altura: 200,
    profundidade: 55,
    
    // Step 3: Configuração
    qtdPortas: 2,
    modeloPorta: 'ripada',
    qtdGavetas: 0,
    posicaoGavetas: 'baixo',
    temNicho: false,
    qtdNichos: 1,
    posicaoNicho: 'centro',
    qtdPrateleiras: 2,
    
    // Ferragens
    tipoDobradiça: 'caneco_35mm',
    tipoPuxador: 'alca_madeira',
    tipoCorredica: 'telescopica',
    
    // Step 4: Materiais
    material_id: '',
    acabamento_id: '',
    
    // Valores
    margem: 30,
    desconto: 0,
    custo_frete: 0,
    prazo_entrega: 30,
    condicoes_pagamento: '50% entrada + 50% na entrega',
    observacoes: '',
  });
  
  // Componentes gerados automaticamente
  const [componentesGerados, setComponentesGerados] = useState([]);
  const [ferragensCalculadas, setFerragensCalculadas] = useState([]);
  
  // Inicializar com dados de edição
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);
  
  // Inicializar material/acabamento padrão
  useEffect(() => {
    if (config && !formData.material_id) {
      const materialAtivo = config.materiais?.find(m => m.ativo);
      const acabamentoAtivo = config.acabamentos?.find(a => a.ativo);
      if (materialAtivo || acabamentoAtivo) {
        setFormData(prev => ({
          ...prev,
          material_id: materialAtivo?.id || prev.material_id,
          acabamento_id: acabamentoAtivo?.id || prev.acabamento_id,
          margem: config.margem_padrao || prev.margem,
          custo_frete: config.frete_padrao || prev.custo_frete,
        }));
      }
    }
  }, [config, formData.material_id]);
  
  // Atualizar dimensões quando tipo de móvel muda
  useEffect(() => {
    if (formData.tipoMovel) {
      const tipoConfig = TIPOS_MOVEL.find(t => t.id === formData.tipoMovel);
      if (tipoConfig) {
        setFormData(prev => ({
          ...prev,
          largura: tipoConfig.defaultLargura,
          altura: tipoConfig.defaultAltura,
          profundidade: tipoConfig.defaultProfundidade,
          qtdGavetas: tipoConfig.sugereGavetas ? 2 : 0,
          temNicho: tipoConfig.sugereNicho,
          ambiente: tipoConfig.id === 'guarda_roupa' ? 'Quarto' : 'Cozinha',
        }));
      }
    }
  }, [formData.tipoMovel]);
  
  // Gerar componentes automaticamente baseado na configuração
  const gerarComponentes = useCallback(() => {
    const { largura, altura, qtdPortas, modeloPorta, qtdGavetas, posicaoGavetas, 
            temNicho, qtdNichos, posicaoNicho, tipoDobradiça, tipoPuxador, tipoCorredica } = formData;
    
    const componentes = [];
    const ferragens = [];
    const margem = 4;
    
    let areaDisponivel = {
      x: margem,
      y: margem,
      largura: largura - margem * 2,
      altura: altura - margem * 2
    };
    
    // Verificar se gavetas estão na lateral
    const gavetasNaLateral = posicaoGavetas === 'esquerda' || posicaoGavetas === 'direita';
    
    // Calcular dimensões das gavetas
    let alturaGavetas = 0;
    let larguraGavetas = 0;
    
    if (qtdGavetas > 0) {
      if (gavetasNaLateral) {
        // Gavetas na lateral ocupam ~35% da largura
        larguraGavetas = Math.min(areaDisponivel.largura * 0.35, 50);
      } else {
        // Gavetas em cima/baixo ocupam altura proporcional
        alturaGavetas = Math.min(qtdGavetas * 20, areaDisponivel.altura * 0.4);
      }
    }
    
    // Calcular dimensões do nicho
    let larguraNicho = 0;
    let alturaNicho = 0;
    if (temNicho && qtdNichos > 0) {
      if (posicaoNicho === 'centro' || posicaoNicho === 'lado') {
        larguraNicho = areaDisponivel.largura * 0.25;
      } else {
        alturaNicho = areaDisponivel.altura * 0.25;
      }
    }
    
    // Área disponível para portas (considerando gavetas laterais)
    const alturaPortas = areaDisponivel.altura - (gavetasNaLateral ? 0 : alturaGavetas) - 
      (posicaoNicho === 'cima' || posicaoNicho === 'baixo' ? alturaNicho : 0);
    
    const larguraPortas = areaDisponivel.largura - (gavetasNaLateral ? larguraGavetas + 2 : 0) -
      (temNicho && (posicaoNicho === 'centro' || posicaoNicho === 'lado') ? larguraNicho + 2 : 0);
    
    // GERAR GAVETAS
    if (qtdGavetas > 0) {
      if (gavetasNaLateral) {
        // Gavetas na lateral (esquerda ou direita)
        const alturaGaveta = (areaDisponivel.altura - (qtdGavetas - 1) * 2) / qtdGavetas;
        const startX = posicaoGavetas === 'esquerda' 
          ? margem 
          : areaDisponivel.largura + margem - larguraGavetas;
        
        for (let i = 0; i < qtdGavetas; i++) {
          componentes.push({
            id: `gaveta_${i}`,
            tipo: 'gaveta',
            largura: larguraGavetas,
            altura: alturaGaveta,
            posicao_x: startX,
            posicao_y: margem + i * (alturaGaveta + 2),
            ferragens: ['corrediça', 'puxador']
          });
          
          ferragens.push({
            tipo: 'corrediça',
            modelo: TIPOS_CORREDICA.find(c => c.id === tipoCorredica)?.label,
            quantidade: 2,
            preco_unit: TIPOS_CORREDICA.find(c => c.id === tipoCorredica)?.preco || 0
          });
          ferragens.push({
            tipo: 'puxador',
            modelo: TIPOS_PUXADOR.find(p => p.id === tipoPuxador)?.label,
            quantidade: 1,
            preco_unit: TIPOS_PUXADOR.find(p => p.id === tipoPuxador)?.preco || 0
          });
        }
      } else {
        // Gavetas em cima ou embaixo
        const alturaGaveta = (alturaGavetas - (qtdGavetas - 1) * 2) / qtdGavetas;
        const larguraGaveta = areaDisponivel.largura;
        
        const startY = posicaoGavetas === 'cima' 
          ? margem 
          : areaDisponivel.altura + margem - alturaGavetas;
        
        for (let i = 0; i < qtdGavetas; i++) {
          componentes.push({
            id: `gaveta_${i}`,
            tipo: 'gaveta',
            largura: larguraGaveta,
            altura: alturaGaveta,
            posicao_x: margem,
            posicao_y: startY + i * (alturaGaveta + 2),
            ferragens: ['corrediça', 'puxador']
          });
          
          ferragens.push({
            tipo: 'corrediça',
            modelo: TIPOS_CORREDICA.find(c => c.id === tipoCorredica)?.label,
            quantidade: 2,
            preco_unit: TIPOS_CORREDICA.find(c => c.id === tipoCorredica)?.preco || 0
          });
          ferragens.push({
            tipo: 'puxador',
            modelo: TIPOS_PUXADOR.find(p => p.id === tipoPuxador)?.label,
            quantidade: 1,
            preco_unit: TIPOS_PUXADOR.find(p => p.id === tipoPuxador)?.preco || 0
          });
        }
      }
    }
    
    // GERAR NICHOS
    if (temNicho && qtdNichos > 0) {
      let nichoX, nichoY, nichoW, nichoH;
      
      // Ajuste para gavetas laterais
      const offsetXGaveta = gavetasNaLateral && posicaoGavetas === 'esquerda' ? larguraGavetas + 2 : 0;
      const larguraDisponivelNicho = gavetasNaLateral ? areaDisponivel.largura - larguraGavetas - 2 : areaDisponivel.largura;
      
      switch (posicaoNicho) {
        case 'centro':
          nichoW = larguraNicho;
          nichoH = alturaPortas;
          nichoX = margem + offsetXGaveta + (larguraDisponivelNicho - nichoW) / 2;
          nichoY = margem + (!gavetasNaLateral && posicaoGavetas === 'cima' ? alturaGavetas + 2 : 0);
          break;
        case 'cima':
          nichoW = larguraDisponivelNicho;
          nichoH = alturaNicho || areaDisponivel.altura * 0.25;
          nichoX = margem + offsetXGaveta;
          nichoY = margem;
          break;
        case 'baixo':
          nichoW = larguraDisponivelNicho;
          nichoH = alturaNicho || areaDisponivel.altura * 0.25;
          nichoX = margem + offsetXGaveta;
          nichoY = areaDisponivel.altura + margem - nichoH - (!gavetasNaLateral ? alturaGavetas : 0);
          break;
        case 'lado':
          nichoW = larguraNicho;
          nichoH = alturaPortas;
          nichoX = margem + offsetXGaveta + larguraDisponivelNicho - nichoW;
          nichoY = margem + (!gavetasNaLateral && posicaoGavetas === 'cima' ? alturaGavetas + 2 : 0);
          break;
        default:
          nichoW = 0; nichoH = 0; nichoX = 0; nichoY = 0;
      }
      
      if (qtdNichos === 1) {
        componentes.push({
          id: 'nicho_0',
          tipo: 'nicho',
          largura: nichoW,
          altura: nichoH,
          posicao_x: nichoX,
          posicao_y: nichoY,
          ferragens: []
        });
      } else {
        const nichoH_individual = (nichoH - (qtdNichos - 1) * 2) / qtdNichos;
        for (let i = 0; i < qtdNichos; i++) {
          componentes.push({
            id: `nicho_${i}`,
            tipo: 'nicho',
            largura: nichoW,
            altura: nichoH_individual,
            posicao_x: nichoX,
            posicao_y: nichoY + i * (nichoH_individual + 2),
            ferragens: []
          });
        }
      }
    }
    
    // GERAR PORTAS
    if (qtdPortas > 0) {
      let larguraDisponivelPortas = areaDisponivel.largura;
      let startXPortas = margem;
      
      // Ajustar largura disponível para gavetas laterais
      if (gavetasNaLateral && qtdGavetas > 0) {
        larguraDisponivelPortas = areaDisponivel.largura - larguraGavetas - 2;
        if (posicaoGavetas === 'esquerda') {
          startXPortas = margem + larguraGavetas + 2;
        }
      }
      
      // Ajustar para nichos
      if (temNicho && (posicaoNicho === 'centro' || posicaoNicho === 'lado')) {
        if (posicaoNicho === 'centro') {
          larguraDisponivelPortas = (larguraDisponivelPortas - larguraNicho - 4) / 2;
        } else {
          larguraDisponivelPortas = larguraDisponivelPortas - larguraNicho - 2;
        }
      }
      
      const tipoPorta = modeloPorta === 'ripada' ? 'porta_ripada' :
                       modeloPorta === 'vidro' ? 'porta_vidro' : 'porta_lisa';
      
      const alturaPorta = alturaPortas - (posicaoGavetas === 'cima' && !gavetasNaLateral ? 2 : 0);
      const startYPortas = margem + (posicaoGavetas === 'cima' && !gavetasNaLateral ? alturaGavetas + 2 : 0) +
                          (posicaoNicho === 'cima' && temNicho ? alturaNicho + 2 : 0);
      
      if (posicaoNicho === 'centro' && temNicho) {
        const portasPorLado = Math.ceil(qtdPortas / 2);
        const larguraPortaLado = (larguraDisponivelPortas - (portasPorLado > 1 ? (portasPorLado - 1) * 2 : 0)) / portasPorLado;
        
        // Portas da esquerda
        for (let i = 0; i < portasPorLado; i++) {
          componentes.push({
            id: `porta_esq_${i}`,
            tipo: tipoPorta,
            largura: larguraPortaLado,
            altura: alturaPorta,
            posicao_x: startXPortas + i * (larguraPortaLado + 2),
            posicao_y: startYPortas,
            ferragens: ['dobradiça', 'puxador']
          });
          
          ferragens.push({
            tipo: 'dobradiça',
            modelo: TIPOS_DOBRADICA.find(d => d.id === tipoDobradiça)?.label,
            quantidade: alturaPorta > 100 ? 3 : 2,
            preco_unit: TIPOS_DOBRADICA.find(d => d.id === tipoDobradiça)?.preco || 0
          });
          ferragens.push({
            tipo: 'puxador',
            modelo: TIPOS_PUXADOR.find(p => p.id === tipoPuxador)?.label,
            quantidade: 1,
            preco_unit: TIPOS_PUXADOR.find(p => p.id === tipoPuxador)?.preco || 0
          });
        }
        
        // Portas da direita
        const nichoEndX = startXPortas + larguraDisponivelPortas + 2 + larguraNicho + 2;
        for (let i = 0; i < (qtdPortas - portasPorLado); i++) {
          componentes.push({
            id: `porta_dir_${i}`,
            tipo: tipoPorta,
            largura: larguraPortaLado,
            altura: alturaPorta,
            posicao_x: nichoEndX + i * (larguraPortaLado + 2),
            posicao_y: startYPortas,
            ferragens: ['dobradiça', 'puxador']
          });
          
          ferragens.push({
            tipo: 'dobradiça',
            modelo: TIPOS_DOBRADICA.find(d => d.id === tipoDobradiça)?.label,
            quantidade: alturaPorta > 100 ? 3 : 2,
            preco_unit: TIPOS_DOBRADICA.find(d => d.id === tipoDobradiça)?.preco || 0
          });
          ferragens.push({
            tipo: 'puxador',
            modelo: TIPOS_PUXADOR.find(p => p.id === tipoPuxador)?.label,
            quantidade: 1,
            preco_unit: TIPOS_PUXADOR.find(p => p.id === tipoPuxador)?.preco || 0
          });
        }
      } else {
        // Portas lado a lado
        const larguraPorta = (larguraDisponivelPortas - (qtdPortas - 1) * 2) / qtdPortas;
        
        for (let i = 0; i < qtdPortas; i++) {
          componentes.push({
            id: `porta_${i}`,
            tipo: tipoPorta,
            largura: larguraPorta,
            altura: alturaPorta,
            posicao_x: startXPortas + i * (larguraPorta + 2),
            posicao_y: startYPortas,
            ferragens: ['dobradiça', 'puxador']
          });
          
          ferragens.push({
            tipo: 'dobradiça',
            modelo: TIPOS_DOBRADICA.find(d => d.id === tipoDobradiça)?.label,
            quantidade: alturaPorta > 100 ? 3 : 2,
            preco_unit: TIPOS_DOBRADICA.find(d => d.id === tipoDobradiça)?.preco || 0
          });
          ferragens.push({
            tipo: 'puxador',
            modelo: TIPOS_PUXADOR.find(p => p.id === tipoPuxador)?.label,
            quantidade: 1,
            preco_unit: TIPOS_PUXADOR.find(p => p.id === tipoPuxador)?.preco || 0
          });
        }
      }
    }
    
    // Agrupar ferragens iguais
    const ferragensAgrupadas = [];
    ferragens.forEach(f => {
      const existente = ferragensAgrupadas.find(
        fa => fa.tipo === f.tipo && fa.modelo === f.modelo
      );
      if (existente) {
        existente.quantidade += f.quantidade;
      } else {
        ferragensAgrupadas.push({ ...f });
      }
    });
    
    setComponentesGerados(componentes);
    setFerragensCalculadas(ferragensAgrupadas);
    
  }, [formData]);
  
  // Regenerar quando config relevante muda
  useEffect(() => {
    if (currentStep >= 2) {
      gerarComponentes();
    }
  }, [currentStep, formData.qtdPortas, formData.modeloPorta, formData.qtdGavetas, 
      formData.posicaoGavetas, formData.temNicho, formData.qtdNichos, formData.posicaoNicho,
      formData.largura, formData.altura, formData.tipoDobradiça, formData.tipoPuxador, 
      formData.tipoCorredica, gerarComponentes]);
  
  // Calcular custos
  const calculos = useMemo(() => {
    if (!config) return { area: 0, custoMaterial: 0, custoTotal: 0, valorVenda: 0, lucro: 0 };
    
    const largura_m = formData.largura / 100;
    const altura_m = formData.altura / 100;
    const profundidade_m = formData.profundidade / 100;
    
    const area_frontal = largura_m * altura_m;
    const area_laterais = 2 * (profundidade_m * altura_m);
    const area_fundo = largura_m * altura_m;
    const area_tampo = largura_m * profundidade_m;
    const area_total = area_frontal + area_laterais + area_fundo + area_tampo;
    
    const material = config.materiais?.find(m => m.id === formData.material_id);
    const acabamento = config.acabamentos?.find(a => a.id === formData.acabamento_id);
    
    const custoMaterial = area_total * ((material?.preco_m2 || 0) + (acabamento?.preco_m2 || 0));
    
    const custoFerragens = ferragensCalculadas.reduce(
      (sum, f) => sum + (f.quantidade * f.preco_unit), 0
    );
    
    const custoMaoDeObra = area_total * 8 * (config.mao_de_obra_hora || 25);
    const custoInstalacao = area_total * (config.instalacao_m2 || 50);
    const custoTotal = custoMaterial + custoFerragens + custoMaoDeObra + custoInstalacao + (formData.custo_frete || 0);
    
    const valorVendaBruto = custoTotal * (1 + (formData.margem || 30) / 100);
    const valorVenda = valorVendaBruto - (formData.desconto || 0);
    const lucro = valorVenda - custoTotal;
    
    return {
      area: area_total.toFixed(2),
      custoMaterial: custoMaterial.toFixed(2),
      custoFerragens: custoFerragens.toFixed(2),
      custoMaoDeObra: custoMaoDeObra.toFixed(2),
      custoInstalacao: custoInstalacao.toFixed(2),
      custoTotal: custoTotal.toFixed(2),
      valorVenda: valorVenda.toFixed(2),
      lucro: lucro.toFixed(2),
      margemReal: custoTotal > 0 ? ((lucro / custoTotal) * 100).toFixed(1) : 0,
    };
  }, [config, formData, ferragensCalculadas]);
  
  // Validação de step
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 0: return !!formData.tipoMovel && !!formData.cliente_id;
      case 1: return formData.largura > 0 && formData.altura > 0 && formData.profundidade > 0;
      case 2: return true;
      case 3: return !!formData.material_id;
      case 4: return true;
      default: return true;
    }
  }, [currentStep, formData]);
  
  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canProceed) {
      setCurrentStep(currentStep + 1);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleComplete = () => {
    const tipoConfig = TIPOS_MOVEL.find(t => t.id === formData.tipoMovel);
    const cliente = customers.find(c => c.id === formData.cliente_id);
    
    const projetoData = {
      cliente_id: formData.cliente_id,
      cliente_nome: cliente?.name || '',
      ambiente: formData.ambiente,
      tipo_movel: tipoConfig?.label || formData.tipoMovel,
      nome_projeto: formData.nome_projeto,
      largura: formData.largura,
      altura: formData.altura,
      profundidade: formData.profundidade,
      material_id: formData.material_id,
      acabamento_id: formData.acabamento_id,
      ferragens_selecionadas: ferragensCalculadas.map(f => ({
        ferragem_id: f.modelo,
        quantidade: f.quantidade,
        preco_unitario: f.preco_unit
      })),
      componentes: componentesGerados,
      margem: formData.margem,
      desconto: formData.desconto,
      custo_frete: formData.custo_frete,
      prazo_entrega: formData.prazo_entrega,
      condicoes_pagamento: formData.condicoes_pagamento,
      observacoes: formData.observacoes,
      // Valores calculados
      area_total: parseFloat(calculos.area),
      custo_material: parseFloat(calculos.custoMaterial),
      custo_ferragens: parseFloat(calculos.custoFerragens),
      custo_mao_de_obra: parseFloat(calculos.custoMaoDeObra),
      custo_instalacao: parseFloat(calculos.custoInstalacao),
      custo_total: parseFloat(calculos.custoTotal),
      valor_venda: parseFloat(calculos.valorVenda),
      lucro: parseFloat(calculos.lucro),
    };
    
    if (onComplete) {
      onComplete(projetoData);
    }
  };
  
  const tipoMovelConfig = TIPOS_MOVEL.find(t => t.id === formData.tipoMovel);

  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between items-center text-sm">
          <span className="text-muted-foreground">Passo {currentStep + 1} de {STEPS.length}</span>
          <span className="font-medium">{STEPS[currentStep].label}</span>
        </div>
        <Progress value={((currentStep + 1) / STEPS.length) * 100} className="h-2" />
        
        {/* Step indicators */}
        <div className="flex justify-between mt-4">
          {STEPS.map((step, idx) => {
            const StepIcon = step.icon;
            const isActive = idx === currentStep;
            const isCompleted = idx < currentStep;
            
            return (
              <div 
                key={step.id}
                className={`flex flex-col items-center gap-1 ${
                  isActive ? 'text-primary' : isCompleted ? 'text-green-500' : 'text-muted-foreground'
                }`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                  isActive ? 'border-primary bg-primary/10' : 
                  isCompleted ? 'border-green-500 bg-green-500/10' : 
                  'border-muted bg-muted/10'
                }`}>
                  {isCompleted ? <Check className="h-5 w-5" /> : <StepIcon className="h-5 w-5" />}
                </div>
                <span className="text-xs hidden sm:block">{step.label}</span>
              </div>
            );
          })}
        </div>
      </div>
      
      <Separator />
      
      {/* Step Content */}
      <div className="min-h-[400px]">
        {/* STEP 0: Tipo de Móvel */}
        {currentStep === 0 && (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label className="text-base font-medium">Qual tipo de móvel você deseja criar?</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {TIPOS_MOVEL.map(tipo => {
                  const Icon = tipo.icon;
                  return (
                    <button
                      key={tipo.id}
                      onClick={() => setFormData({...formData, tipoMovel: tipo.id})}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        formData.tipoMovel === tipo.id 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`tipo-movel-${tipo.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${
                          formData.tipoMovel === tipo.id ? 'bg-primary/20' : 'bg-muted'
                        }`}>
                          <Icon className="h-6 w-6" />
                        </div>
                        <div>
                          <p className="font-medium">{tipo.label}</p>
                          <p className="text-sm text-muted-foreground">{tipo.desc}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Padrão: {tipo.defaultLargura}×{tipo.defaultAltura}×{tipo.defaultProfundidade} cm
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <Select 
                  value={formData.cliente_id} 
                  onValueChange={(v) => setFormData({...formData, cliente_id: v})}
                >
                  <SelectTrigger data-testid="wizard-select-cliente">
                    <SelectValue placeholder="Selecione o cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Nome do Projeto (opcional)</Label>
                <Input
                  value={formData.nome_projeto}
                  onChange={(e) => setFormData({...formData, nome_projeto: e.target.value})}
                  placeholder="Ex: Cozinha Planejada Cliente X"
                />
              </div>
            </div>
          </div>
        )}
        
        {/* STEP 1: Dimensões */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg">
              <Sparkles className="h-5 w-5 text-primary" />
              <span className="text-sm">
                Dimensões padrão para <strong>{tipoMovelConfig?.label}</strong>. Ajuste conforme necessário.
              </span>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Largura (cm)
                </Label>
                <Input
                  type="number"
                  value={formData.largura}
                  onChange={(e) => setFormData({...formData, largura: parseFloat(e.target.value) || 0})}
                  data-testid="wizard-input-largura"
                />
                <p className="text-xs text-muted-foreground">Sugerido: {tipoMovelConfig?.defaultLargura} cm</p>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Altura (cm)
                </Label>
                <Input
                  type="number"
                  value={formData.altura}
                  onChange={(e) => setFormData({...formData, altura: parseFloat(e.target.value) || 0})}
                  data-testid="wizard-input-altura"
                />
                <p className="text-xs text-muted-foreground">Sugerido: {tipoMovelConfig?.defaultAltura} cm</p>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Profundidade (cm)
                </Label>
                <Input
                  type="number"
                  value={formData.profundidade}
                  onChange={(e) => setFormData({...formData, profundidade: parseFloat(e.target.value) || 0})}
                  data-testid="wizard-input-profundidade"
                />
                <p className="text-xs text-muted-foreground">Sugerido: {tipoMovelConfig?.defaultProfundidade} cm</p>
              </div>
            </div>
            
            {/* Visual Preview das dimensões */}
            <Card className="border-dashed">
              <CardContent className="p-4">
                <div className="flex items-center justify-center">
                  <div 
                    className="border-2 border-primary/50 bg-primary/5 rounded flex items-center justify-center relative"
                    style={{
                      width: Math.min(300, formData.largura * 1.5),
                      height: Math.min(200, formData.altura * 0.8),
                    }}
                  >
                    <span className="text-sm text-muted-foreground">
                      {formData.largura} × {formData.altura} × {formData.profundidade} cm
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* STEP 2: Configuração */}
        {currentStep === 2 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              {/* PORTAS */}
              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <DoorOpen className="h-4 w-4 text-primary" />
                    Quantas portas?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-10 w-10 p-0 text-lg"
                        onClick={() => setFormData(f => ({...f, qtdPortas: Math.max(0, f.qtdPortas - 1)}))}
                      >-</Button>
                      <span className="w-12 text-center text-2xl font-bold">{formData.qtdPortas}</span>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-10 w-10 p-0 text-lg"
                        onClick={() => setFormData(f => ({...f, qtdPortas: Math.min(tipoMovelConfig?.maxPortas || 6, f.qtdPortas + 1)}))}
                      >+</Button>
                    </div>
                  </div>
                  
                  {formData.qtdPortas > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Modelo da Porta:</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {MODELOS_PORTA.map(modelo => {
                          const Icon = modelo.icon;
                          return (
                            <button
                              key={modelo.id}
                              onClick={() => setFormData(f => ({...f, modeloPorta: modelo.id}))}
                              className={`p-3 rounded-lg border-2 text-center transition-all ${
                                formData.modeloPorta === modelo.id 
                                  ? 'border-primary bg-primary/10' 
                                  : 'border-border hover:border-primary/50'
                              }`}
                            >
                              <Icon className="h-6 w-6 mx-auto mb-1" />
                              <span className="text-xs">{modelo.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* GAVETAS */}
              {(tipoMovelConfig?.maxGavetas || 0) > 0 && (
                <Card className="border-border">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Archive className="h-4 w-4 text-primary" />
                      Quantas gavetas?
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-10 w-10 p-0 text-lg"
                          onClick={() => setFormData(f => ({...f, qtdGavetas: Math.max(0, f.qtdGavetas - 1)}))}
                        >-</Button>
                        <span className="w-12 text-center text-2xl font-bold">{formData.qtdGavetas}</span>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="h-10 w-10 p-0 text-lg"
                          onClick={() => setFormData(f => ({...f, qtdGavetas: Math.min(tipoMovelConfig?.maxGavetas || 6, f.qtdGavetas + 1)}))}
                        >+</Button>
                      </div>
                    </div>
                    
                    {formData.qtdGavetas > 0 && (
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Posição das Gavetas:</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { id: 'baixo', label: 'Embaixo' },
                            { id: 'cima', label: 'Em cima' },
                            { id: 'esquerda', label: 'Lateral Esq.' },
                            { id: 'direita', label: 'Lateral Dir.' },
                          ].map(pos => (
                            <Button
                              key={pos.id}
                              size="sm"
                              variant={formData.posicaoGavetas === pos.id ? 'default' : 'outline'}
                              onClick={() => setFormData(f => ({...f, posicaoGavetas: pos.id}))}
                            >
                              {pos.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* NICHOS */}
              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Square className="h-4 w-4 text-primary" />
                    Espaço Aberto (Nicho)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Checkbox 
                      id="temNicho" 
                      checked={formData.temNicho}
                      onCheckedChange={v => setFormData(f => ({...f, temNicho: v}))}
                    />
                    <Label htmlFor="temNicho" className="cursor-pointer">
                      Adicionar espaço aberto
                    </Label>
                  </div>
                  
                  {formData.temNicho && (
                    <div className="space-y-3 pt-2">
                      <div className="flex items-center gap-4">
                        <Label className="text-xs text-muted-foreground w-20">Quantidade:</Label>
                        <div className="flex items-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-8 w-8 p-0"
                            onClick={() => setFormData(f => ({...f, qtdNichos: Math.max(1, f.qtdNichos - 1)}))}
                          >-</Button>
                          <span className="w-8 text-center font-bold">{formData.qtdNichos}</span>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-8 w-8 p-0"
                            onClick={() => setFormData(f => ({...f, qtdNichos: Math.min(4, f.qtdNichos + 1)}))}
                          >+</Button>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Posição:</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {[
                            { id: 'centro', label: 'Centro' },
                            { id: 'cima', label: 'Em cima' },
                            { id: 'baixo', label: 'Embaixo' },
                            { id: 'lado', label: 'Lateral' },
                          ].map(pos => (
                            <Button
                              key={pos.id}
                              size="sm"
                              variant={formData.posicaoNicho === pos.id ? 'default' : 'outline'}
                              onClick={() => setFormData(f => ({...f, posicaoNicho: pos.id}))}
                            >
                              {pos.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Preview do desenho */}
            <div className="space-y-4">
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Preview Automático
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FurniturePreview 
                    largura={formData.largura}
                    altura={formData.altura}
                    componentes={componentesGerados}
                  />
                </CardContent>
              </Card>
              
              {/* Ferragens calculadas */}
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      Ferragens Automáticas
                    </span>
                    <Badge variant="outline">{ferragensCalculadas.length} tipos</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {ferragensCalculadas.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      Configure o móvel para calcular ferragens
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {ferragensCalculadas.map((f, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span>{f.modelo} ({f.tipo})</span>
                          <span className="font-medium">{f.quantidade}x</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
        
        {/* STEP 3: Materiais */}
        {currentStep === 3 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Material Principal *
                </Label>
                <div className="grid grid-cols-1 gap-2">
                  {config?.materiais?.filter(m => m.ativo).map(mat => (
                    <button
                      key={mat.id}
                      onClick={() => setFormData({...formData, material_id: mat.id})}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.material_id === mat.id 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{mat.nome}</span>
                        <span className="text-sm text-muted-foreground">{formatCurrency(mat.preco_m2)}/m²</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <PaintBucket className="h-4 w-4" />
                  Acabamento
                </Label>
                <div className="grid grid-cols-1 gap-2">
                  {config?.acabamentos?.filter(a => a.ativo).map(acab => (
                    <button
                      key={acab.id}
                      onClick={() => setFormData({...formData, acabamento_id: acab.id})}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.acabamento_id === acab.id 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="font-medium">{acab.nome}</span>
                        <span className="text-sm text-muted-foreground">+{formatCurrency(acab.preco_m2)}/m²</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Margem de Lucro (%)</Label>
                  <Input
                    type="number"
                    value={formData.margem}
                    onChange={(e) => setFormData({...formData, margem: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Frete (R$)</Label>
                  <Input
                    type="number"
                    value={formData.custo_frete}
                    onChange={(e) => setFormData({...formData, custo_frete: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            </div>
            
            {/* Resumo de custos */}
            <div>
              <Card className="border-primary/20 bg-primary/5">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calculator className="h-4 w-4" />
                    Cálculo Automático
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Área Total:</span>
                    <span className="font-medium">{calculos.area} m²</span>
                  </div>
                  <Separator />
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Material:</span>
                      <span>{formatCurrency(parseFloat(calculos.custoMaterial))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Ferragens:</span>
                      <span>{formatCurrency(parseFloat(calculos.custoFerragens))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Mão de Obra:</span>
                      <span>{formatCurrency(parseFloat(calculos.custoMaoDeObra))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Instalação:</span>
                      <span>{formatCurrency(parseFloat(calculos.custoInstalacao))}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Frete:</span>
                      <span>{formatCurrency(formData.custo_frete || 0)}</span>
                    </div>
                  </div>
                  <Separator />
                  <div className="flex justify-between font-medium">
                    <span>Custo Total:</span>
                    <span>{formatCurrency(parseFloat(calculos.custoTotal))}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between items-center pt-2">
                    <span className="font-semibold">Valor de Venda:</span>
                    <span className="text-2xl font-bold text-primary">{formatCurrency(parseFloat(calculos.valorVenda))}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Lucro:</span>
                    <span className={`font-medium ${parseFloat(calculos.lucro) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {formatCurrency(parseFloat(calculos.lucro))} ({calculos.margemReal}%)
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
        
        {/* STEP 4: Resumo Final */}
        {currentStep === 4 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Dados do Projeto</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tipo:</span>
                    <span className="font-medium">{tipoMovelConfig?.label}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Cliente:</span>
                    <span className="font-medium">{customers.find(c => c.id === formData.cliente_id)?.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Dimensões:</span>
                    <span className="font-medium">{formData.largura}×{formData.altura}×{formData.profundidade} cm</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Portas:</span>
                    <span className="font-medium">{formData.qtdPortas} ({MODELOS_PORTA.find(m => m.id === formData.modeloPorta)?.label})</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Gavetas:</span>
                    <span className="font-medium">{formData.qtdGavetas}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Nichos:</span>
                    <span className="font-medium">{formData.temNicho ? formData.qtdNichos : 'Não'}</span>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Detalhes Adicionais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Prazo de Entrega (dias)</Label>
                      <Input
                        type="number"
                        value={formData.prazo_entrega}
                        onChange={(e) => setFormData({...formData, prazo_entrega: parseInt(e.target.value) || 30})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Desconto (R$)</Label>
                      <Input
                        type="number"
                        value={formData.desconto}
                        onChange={(e) => setFormData({...formData, desconto: parseFloat(e.target.value) || 0})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Condições de Pagamento</Label>
                    <Input
                      value={formData.condicoes_pagamento}
                      onChange={(e) => setFormData({...formData, condicoes_pagamento: e.target.value})}
                      placeholder="Ex: 50% entrada + 50% na entrega"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Observações</Label>
                    <Input
                      value={formData.observacoes}
                      onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                      placeholder="Observações gerais..."
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <div className="space-y-4">
              {/* Preview Final */}
              <Card className="border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Visualização do Móvel
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FurniturePreview 
                    largura={formData.largura}
                    altura={formData.altura}
                    componentes={componentesGerados}
                  />
                </CardContent>
              </Card>
              
              {/* Valor Final */}
              <Card className="border-green-500/30 bg-green-500/5">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Valor Final do Orçamento</p>
                      <p className="text-3xl font-bold text-green-600">{formatCurrency(parseFloat(calculos.valorVenda))}</p>
                    </div>
                    <CircleDollarSign className="h-12 w-12 text-green-500/50" />
                  </div>
                  {formData.desconto > 0 && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Desconto aplicado: -{formatCurrency(formData.desconto)}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
      
      {/* Navigation Buttons */}
      <Separator />
      <div className="flex justify-between">
        <div>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Cancelar
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {currentStep > 0 && (
            <Button variant="outline" onClick={handleBack}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Voltar
            </Button>
          )}
          {currentStep < STEPS.length - 1 ? (
            <Button onClick={handleNext} disabled={!canProceed} data-testid="wizard-btn-proximo">
              Próximo
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleComplete} className="bg-green-600 hover:bg-green-700" data-testid="wizard-btn-criar">
              <Check className="h-4 w-4 mr-1" />
              Criar Projeto
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Componente de Preview do Móvel
function FurniturePreview({ largura, altura, componentes }) {
  const PREVIEW_WIDTH = 300;
  const PREVIEW_HEIGHT = 220;
  const PADDING = 20;
  
  // Calcular escala
  const scaleX = (PREVIEW_WIDTH - PADDING * 2) / largura;
  const scaleY = (PREVIEW_HEIGHT - PADDING * 2) / altura;
  const scale = Math.min(scaleX, scaleY);
  
  const scaledWidth = largura * scale;
  const scaledHeight = altura * scale;
  const offsetX = (PREVIEW_WIDTH - scaledWidth) / 2;
  const offsetY = (PREVIEW_HEIGHT - scaledHeight) / 2;
  
  const getComponentColor = (tipo) => {
    switch (tipo) {
      case 'porta_ripada': return '#B8860B';
      case 'porta_lisa': return '#CD853F';
      case 'porta_vidro': return 'rgba(135, 206, 235, 0.4)';
      case 'gaveta': return '#DAA520';
      case 'nicho': return '#2a2a2a';
      default: return '#8B7355';
    }
  };
  
  const getComponentBorder = (tipo) => {
    switch (tipo) {
      case 'porta_vidro': return '2px solid #8B7355';
      default: return '1px solid #5D4037';
    }
  };

  return (
    <div 
      className="relative bg-zinc-900 rounded-lg overflow-hidden" 
      style={{ width: PREVIEW_WIDTH, height: PREVIEW_HEIGHT }}
    >
      {/* Grid de fundo */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}
      />
      
      {/* Corpo do móvel */}
      <div
        className="absolute bg-gradient-to-br from-amber-900/80 to-amber-950 rounded shadow-xl"
        style={{
          left: offsetX,
          top: offsetY,
          width: scaledWidth,
          height: scaledHeight,
          border: '2px solid #5D4037'
        }}
      >
        {/* Componentes */}
        {componentes.map((comp) => (
          <div
            key={comp.id}
            className="absolute transition-all duration-200"
            style={{
              left: comp.posicao_x * scale,
              top: comp.posicao_y * scale,
              width: comp.largura * scale,
              height: comp.altura * scale,
              backgroundColor: getComponentColor(comp.tipo),
              border: getComponentBorder(comp.tipo),
              borderRadius: '2px',
            }}
          >
            {/* Ripas para porta ripada */}
            {comp.tipo === 'porta_ripada' && (
              <div className="absolute inset-1 flex gap-0.5">
                {Array.from({ length: Math.max(3, Math.floor(comp.largura * scale / 8)) }).map((_, i) => (
                  <div 
                    key={i} 
                    className="flex-1 bg-gradient-to-r from-amber-700 to-amber-800 rounded-sm"
                    style={{ minWidth: 2 }}
                  />
                ))}
              </div>
            )}
            
            {/* Reflexo para porta de vidro */}
            {comp.tipo === 'porta_vidro' && (
              <div 
                className="absolute inset-2 bg-gradient-to-br from-white/20 to-transparent rounded"
              />
            )}
            
            {/* Puxador para portas e gavetas */}
            {(comp.tipo.includes('porta') || comp.tipo === 'gaveta') && (
              <div 
                className="absolute bg-amber-950 rounded-sm shadow"
                style={{
                  width: comp.tipo === 'gaveta' ? Math.min(20, comp.largura * scale * 0.3) : 4,
                  height: comp.tipo === 'gaveta' ? 4 : Math.min(20, comp.altura * scale * 0.2),
                  right: comp.tipo === 'gaveta' ? '50%' : 4,
                  top: comp.tipo === 'gaveta' ? '50%' : '50%',
                  transform: comp.tipo === 'gaveta' ? 'translate(50%, -50%)' : 'translateY(-50%)'
                }}
              />
            )}
          </div>
        ))}
      </div>
      
      {/* Dimensões */}
      <div className="absolute bottom-2 left-2 text-xs text-zinc-400">
        {largura}×{altura} cm | {componentes.length} componentes
      </div>
    </div>
  );
}
